import { useCallback, useEffect, useRef, useState } from "react";
import { getHealth } from "@/api/endpoints";
import { isClientBuildStale } from "@/lib/buildInfo";
import type { BuildIdentityAttributes } from "@/types/api";

interface ClientBuildFreshnessOptions {
  intervalMs?: number;
}

export interface ClientBuildFreshnessState {
  expectedBuild: BuildIdentityAttributes | null;
  stale: boolean;
  lastCheckedAt: string | null;
  error: Error | null;
}

interface ClientBuildFreshness extends ClientBuildFreshnessState {
  refresh: () => Promise<ClientBuildFreshnessState>;
}

const DEFAULT_CHECK_INTERVAL_MS = 60_000;

export function useClientBuildFreshness(options: ClientBuildFreshnessOptions = {}): ClientBuildFreshness {
  const { intervalMs = DEFAULT_CHECK_INTERVAL_MS } = options;
  const [state, setState] = useState<ClientBuildFreshnessState>({
    expectedBuild: null,
    stale: false,
    lastCheckedAt: null,
    error: null,
  });
  const stateRef = useRef(state);
  const mountedRef = useRef(false);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    try {
      const next = await resolveClientBuildFreshness();
      if (mountedRef.current) {
        setState(next);
      }
      return next;
    } catch (error) {
      const next = {
        ...stateRef.current,
        error: error instanceof Error ? error : new Error("Build freshness check failed"),
      };
      if (mountedRef.current) {
        setState(next);
      }
      return next;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function checkFreshness() {
      const next = await refresh();
      if (cancelled) {
        return;
      }
      stateRef.current = next;
    }

    void checkFreshness();
    const timer = window.setInterval(() => {
      void checkFreshness();
    }, intervalMs);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [intervalMs, refresh]);

  return { ...state, refresh };
}

async function resolveClientBuildFreshness(): Promise<ClientBuildFreshnessState> {
  const [deployedBuildResult, healthResult] = await Promise.allSettled([getDeployedWebBuild(), getHealth()]);
  const deployedBuild = deployedBuildResult.status === "fulfilled" ? deployedBuildResult.value : null;
  const healthBuild =
    healthResult.status === "fulfilled"
      ? (healthResult.value.data.attributes.expected_web_client_build ?? null)
      : null;
  const expectedBuild = deployedBuild ?? healthBuild;
  if (!expectedBuild && deployedBuildResult.status === "rejected" && healthResult.status === "rejected") {
    throw deployedBuildResult.reason instanceof Error
      ? deployedBuildResult.reason
      : new Error("Build freshness check failed");
  }
  return {
    expectedBuild,
    stale: isClientBuildStale(expectedBuild),
    lastCheckedAt: new Date().toISOString(),
    error: null,
  };
}

async function getDeployedWebBuild(): Promise<BuildIdentityAttributes | null> {
  const response = await fetch(`/build-info.json?t=${encodeURIComponent(Date.now().toString())}`, {
    cache: "no-store",
  });
  if (!isBuildInfoResponse(response)) {
    return null;
  }
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error(`Build freshness check failed with HTTP ${response.status}`);
  }
  const value: unknown = await response.json();
  if (!value || typeof value !== "object") {
    return null;
  }
  const build = value as Partial<Record<"version" | "commit" | "build_date" | "build_id", unknown>>;
  return {
    version: stringValue(build.version),
    commit: stringValue(build.commit),
    date: stringValue(build.build_date),
    build_id: stringValue(build.build_id),
  };
}

function stringValue(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function isBuildInfoResponse(value: unknown): value is Response {
  return !!value
    && typeof value === "object"
    && "status" in value
    && "ok" in value
    && "json" in value
    && typeof (value as { json?: unknown }).json === "function";
}
